import { memo } from 'react'
import { FormattedMessage } from 'react-intl'
import MemoryFilePreviewCard, { MyFilePreview } from 'components/MemoryFilePreviewCard'
import { ConnectedFileInput } from 'components/FileHelpers/ConnectedFileInput'
import { MyFile } from 'interfaces'
import { DEFAULT_STUDIO_EXPORT_FILE_NAME } from 'utils/file'
import { mapFileTypeToSizeLimit } from 'utils/file/helpers'
import { bytesToSize } from 'shared'
import { noop } from 'utils/noop'
import styles from './MultiUploadManager.pcss'

export interface UploadFilesPanelProps {
  files: { [name: string]: File }
  fileRotation: { [name: string]: string }
  fileSizeLimits: { [key: string]: number }
  copyFile?: MyFile
  onClearCopyFile: () => void
  onAddMoreFiles: (files: { [name: string]: File }) => void
  onFileError: (fileName: string, error: string) => void
  onToggleRotation: (fileName: string) => void
  onRemoveFile: (fileName: string) => void
  onFileLoaded: (name: string, src: string) => void
}

export const UploadFilesPanel = memo(function UploadFilesPanel({
  files,
  fileRotation,
  fileSizeLimits,
  copyFile,
  onClearCopyFile,
  onAddMoreFiles,
  onFileError,
  onToggleRotation,
  onRemoveFile,
  onFileLoaded
}: UploadFilesPanelProps) {
  const filesCount = Object.keys(files).length
  const filesTotalSize = Object.values(files).reduce((total, file) => total + file.size, 0)
  const filesTotalSizeFormatted = bytesToSize(filesTotalSize)

  return (
    <div className={styles['panel-left']}>
      <div className={styles['heading-left']}>
        <h3 className={styles.title}>
          <FormattedMessage id="label.generic.files" />: {filesCount}
        </h3>
        {!copyFile && (
          <ConnectedFileInput
            children={(
              <div className={styles['btn-add']}>
                <span>+ <FormattedMessage id="uploads.actions.add-more-files" /></span>
              </div>
            )}
            inputId="file-upload-popup-select"
            openFileDialog={noop}
            setUploaderFiles={onAddMoreFiles}
          />
        )}
      </div>
      {!copyFile && (
        <p className={styles.stats}>
          <span>{filesCount} <FormattedMessage id="general.labels.files" values={{ count: filesCount }} /> </span>
          <span>{`(${filesTotalSizeFormatted})`}</span>
        </p>
      )}
      <div className={styles['files-grid']}>
        {copyFile && (
          <MyFilePreview
            key="external"
            file={copyFile}
            className={styles['grid-item']}
            onDelete={onClearCopyFile}
          />
        )}
        {!copyFile && Object.values(files).map(f => (
          <MemoryFilePreviewCard
            key={f.name}
            file={f}
            validation={{ maxSize: mapFileTypeToSizeLimit(f.type, fileSizeLimits) }}
            noValidation={f.name === DEFAULT_STUDIO_EXPORT_FILE_NAME}
            rotateBy={fileRotation[f.name]}
            className={styles['grid-item']}
            mediaElementId={f.name}
            onError={onFileError}
            onRotate={onToggleRotation}
            onDelete={onRemoveFile}
            onImageLoaded={onFileLoaded}
          />
        ))}
      </div>
    </div>
  )
})

export default UploadFilesPanel
